import { Card, Stack, Typography, Box, IconButton, Grid, Container } from "@mui/material";
import { LinkedIn, GitHub } from "@mui/icons-material/";

const About = () => {
    const people = [
        {
            name: "Hunter Taylor, Ph.D.",
            imageUrl: "hunter_tailor.jpg",
            role: "Founder & CEO",
            description:
                "Hunter earned his Ph.D. in Mechanical Engineering from the University of Texas at El Paso, where he immersed himself within the W.M. Keck Center, working on laser powder bed fusion process sensitivity and alloy development. He loves to look at melt-pools and microstructures while determining optimal processing parameters. Outside of Tailored Alloys, he is an elite Ultimate frisbee athlete and devoted husband.",
            linkedin: "https://www.linkedin.com/in/hunter-taylor-2b080ba3/",
            github: "",
        },
        {
            name: "Laszlo Kecskes, Ph.D.",
            imageUrl: "laszlo_kecskes.jpg",
            role: "Founder & CSO",
            description:
                "When he is not on the electron microscope or in the lab making samples, Laszlo is on the lookout for new challenges and opportunities for Tailored Alloys. Additionally, he lends his materials science expertise, guides interns on scientific methods and experimentation, or provides hands on training with unique equipment. Outside of his technical responsibilities, he divides his time between exploring the fine arts world, family, classical music theory, and playing the piano badly.",
            linkedin: "",
            github: "",
        },
        {
            name: "Amiel Bernal, Ph.D.",
            imageUrl: "amiel_bernal.jpg",
            role: "COO, Tailored Alloys & Tailored Rings",
            description:
                "Amiel is the glue that sticks Tailored Alloys together. Wearing many hats, he's busy running admin, operations, overseeing projects, and doing end-to-end service for Tailored Rings. As a fan of systems, Amiel strives to set up teams and projects so that they can be successful with few pivots and interventions.",
            linkedin: "https://www.linkedin.com/in/amiel-bernal/",
            github: "",
        },
        {
            name: "Brayant Lopez",
            imageUrl: "brayant_lopez.jpg",
            role: "Software Developer",
            description:
                "Brayant Lopez is a software developer at Tailored Alloys. His work centers around NoSQL schema design and image processing R&D for metallography (refractory alloy) applications. He likes his code the same way as his food... without bugs.",
            linkedin: "https://www.linkedin.com/in/brayantlopez17/",
            github: "https://github.com/brayantlopez17",
        },
        {
            name: "Daniyal Khan",
            imageUrl: "daniyal_khan.jpg",
            role: "Software Engineer",
            description:
                "Daniyal is a software developer at Tailored Alloys. He is responsible for developing and maintaining the company's software infrastructure. He is passionate about software development and is always looking for ways to improve his skills. He likes his books and work desk as much as he likes to code.",
            linkedin: "https://www.linkedin.com/in/daniyalk20/",
            github: "https://github.com/daniyalk20",
        }
    ];

    const TeamCard = ({ person, index }) => {
        return (
            <Grid 
                item 
                xs={12} 
                sm={6} 
                md={4}
                sx={{
                    animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`,
                }}
            >
                <Card
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: '16px',
                        boxShadow: '0 6px 24px rgba(108, 152, 173, 0.12)',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        border: '1px solid rgba(108, 152, 173, 0.08)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                            transform: 'translateY(-12px)',
                            boxShadow: '0 16px 40px rgba(108, 152, 173, 0.25)',
                            border: '1px solid rgba(108, 152, 173, 0.3)',
                            '& .team-image': {
                                transform: 'scale(1.05)',
                            },
                            '& .image-overlay': {
                                opacity: 1,
                            }
                        }
                    }}
                >
                    {/* Image Container */}
                    <Box
                        sx={{
                            position: 'relative',
                            overflow: 'hidden',
                            height: '280px',
                            backgroundColor: '#f5f5f5',
                        }}
                    >
                        <img
                            src={process.env.PUBLIC_URL + "/static/images/" + person.imageUrl}
                            alt={person.name}
                            className="team-image"
                            style={{
                                width: "100%",
                                height: '100%',
                                objectFit: 'contain',
                                objectPosition: 'center top',
                                transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                        />
                        {/* Gradient Overlay */}
                        <Box
                            className="image-overlay"
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'linear-gradient(180deg, transparent 50%, rgba(108, 152, 173, 0.15) 100%)',
                                opacity: 0,
                                transition: 'opacity 0.4s ease',
                                pointerEvents: 'none',
                            }}
                        />
                    </Box>

                    {/* Content */}
                    <Stack
                        spacing={2}
                        sx={{
                            padding: '24px 20px',
                            flexGrow: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                        }}
                    >
                        {/* Name and Description */}
                        <Box>
                            <Typography
                                variant="h5"
                                component="h3"
                                sx={{
                                    color: '#24272a',
                                    fontWeight: 800,
                                    fontSize: '1.35rem',
                                    lineHeight: 1.3,
                                    marginBottom: '4px',
                                    background: 'linear-gradient(135deg, #24272a 0%, #6C98AD 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                }}
                            >
                                {person.name}
                            </Typography>

                            <Typography
                                variant="subtitle1"
                                sx={{
                                    color: '#6C98AD',
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    marginBottom: '16px',
                                }}
                            >
                                {person.role}
                            </Typography>

                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#4a4a4a',
                                    lineHeight: 1.7,
                                    fontSize: '0.95rem',
                                }}
                            >
                                {person.description}
                            </Typography>
                        </Box>

                        {/* Bottom Section */}
                        {(person.linkedin || person.github) && (
                            <Box>
                                {/* Divider */}
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: '1px',
                                        background: 'linear-gradient(90deg, transparent 0%, rgba(108, 152, 173, 0.3) 50%, transparent 100%)',
                                        margin: '16px 0 20px 0',
                                    }}
                                />

                                {/* Social Links */}
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    justifyContent="center"
                                    alignItems="center"
                                >
                                    {person.linkedin && (
                                        <IconButton
                                            size="small"
                                            href={person.linkedin}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                color: '#6C98AD',
                                                border: '2px solid rgba(108, 152, 173, 0.3)',
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    color: 'white',
                                                    background: '#6C98AD',
                                                    borderColor: '#6C98AD',
                                                    transform: 'translateY(-3px) scale(1.05)',
                                                    boxShadow: '0 6px 16px rgba(108, 152, 173, 0.3)',
                                                }
                                            }}
                                        >
                                            <LinkedIn fontSize="small" />
                                        </IconButton>
                                    )}
                                    {person.github && (
                                        <IconButton
                                            size="small"
                                            href={person.github}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                color: '#6C98AD',
                                                border: '2px solid rgba(108, 152, 173, 0.3)',
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    color: 'white',
                                                    background: '#24272a',
                                                    borderColor: '#24272a',
                                                    transform: 'translateY(-3px) scale(1.05)',
                                                    boxShadow: '0 6px 16px rgba(36, 39, 42, 0.3)',
                                                }
                                            }}
                                        >
                                            <GitHub fontSize="small" />
                                        </IconButton>
                                    )}
                                </Stack>
                            </Box>
                        )}
                    </Stack>
                </Card>
            </Grid>
        );
    };

    return (
        <Box className="about-section" sx={{ padding: '40px 20px 60px 20px' }}>
            <Container maxWidth="xl">
                <Grid 
                    container 
                    spacing={4}
                    sx={{
                        justifyContent: 'center',
                    }}
                >
                    {people.map((person, index) => (
                        <TeamCard key={person.name} person={person} index={index} />
                    ))}
                </Grid>
            </Container>
        </Box>
    );
};

export default About;
